import type { MediaKind } from './types';

export type Container =
  | 'mp4'
  | 'mov'
  | 'm4a'
  | 'webm'
  | 'mkv'
  | 'ogg'
  | 'mp3'
  | 'aac'
  | 'wav'
  | 'flac'
  | 'avi'
  | 'png'
  | 'jpeg'
  | 'gif'
  | 'webp'
  | 'avif'
  | 'heic'
  | 'bmp'
  | 'ico'
  | 'svg'
  | 'pdf'
  | 'zip'
  | 'text'
  | 'unknown';

export interface Sniff {
  kind: MediaKind;
  container: Container;
  mime: string | null;
}

const ascii = (b: Uint8Array, at: number, len: number): string => {
  let s = '';
  for (let i = at; i < Math.min(b.length, at + len); i++) s += String.fromCharCode(b[i]!);
  return s;
};

/** Minimum prefix length needed for a confident verdict. */
export const SNIFF_BYTES = 4096;

/**
 * Identify a file from its leading bytes. Magic bytes outrank headers and
 * extensions. Returns `unknown` when the prefix is too short. D01-F10
 */
export function sniffBytes(b: Uint8Array): Sniff {
  if (b.length < 4) return { kind: 'unknown', container: 'unknown', mime: null };

  if (b.length >= 12 && ascii(b, 4, 4) === 'ftyp') {
    const brand = ascii(b, 8, 4);
    if (brand === 'qt  ') return { kind: 'video', container: 'mov', mime: 'video/quicktime' };
    if (brand === 'M4A ' || brand === 'M4B ')
      return { kind: 'audio', container: 'm4a', mime: 'audio/mp4' };
    if (brand === 'avif' || brand === 'avis')
      return { kind: 'image', container: 'avif', mime: 'image/avif' };
    if (['heic', 'heix', 'hevc', 'mif1', 'msf1'].includes(brand)) {
      return { kind: 'image', container: 'heic', mime: 'image/heic' };
    }
    return { kind: 'video', container: 'mp4', mime: 'video/mp4' };
  }
  if (b[0] === 0x1a && b[1] === 0x45 && b[2] === 0xdf && b[3] === 0xa3) {
    const head = ascii(b, 0, Math.min(b.length, 128));
    return head.includes('webm')
      ? { kind: 'video', container: 'webm', mime: 'video/webm' }
      : { kind: 'video', container: 'mkv', mime: 'video/x-matroska' };
  }
  if (ascii(b, 0, 4) === 'OggS') return { kind: 'audio', container: 'ogg', mime: 'audio/ogg' };
  if (ascii(b, 0, 4) === 'fLaC') return { kind: 'audio', container: 'flac', mime: 'audio/flac' };
  if (ascii(b, 0, 4) === 'RIFF' && b.length >= 12) {
    const t = ascii(b, 8, 4);
    if (t === 'WAVE') return { kind: 'audio', container: 'wav', mime: 'audio/wav' };
    if (t === 'WEBP') return { kind: 'image', container: 'webp', mime: 'image/webp' };
    if (t === 'AVI ') return { kind: 'video', container: 'avi', mime: 'video/x-msvideo' };
  }
  if (ascii(b, 0, 3) === 'ID3') return { kind: 'audio', container: 'mp3', mime: 'audio/mpeg' };
  if (b[0] === 0xff && (b[1]! & 0xf6) === 0xf0)
    return { kind: 'audio', container: 'aac', mime: 'audio/aac' };
  if (b[0] === 0xff && (b[1]! & 0xe0) === 0xe0 && (b[1]! & 0x06) !== 0) {
    return { kind: 'audio', container: 'mp3', mime: 'audio/mpeg' };
  }
  if (b[0] === 0x89 && ascii(b, 1, 3) === 'PNG')
    return { kind: 'image', container: 'png', mime: 'image/png' };
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff)
    return { kind: 'image', container: 'jpeg', mime: 'image/jpeg' };
  if (ascii(b, 0, 3) === 'GIF') return { kind: 'image', container: 'gif', mime: 'image/gif' };
  if (ascii(b, 0, 2) === 'BM') return { kind: 'image', container: 'bmp', mime: 'image/bmp' };
  if (b[0] === 0 && b[1] === 0 && b[2] === 1 && b[3] === 0)
    return { kind: 'image', container: 'ico', mime: 'image/x-icon' };
  if (ascii(b, 0, 5) === '%PDF-') return { kind: 'pdf', container: 'pdf', mime: 'application/pdf' };
  if (b[0] === 0x50 && b[1] === 0x4b && (b[2] === 3 || b[2] === 5)) {
    return { kind: 'archive', container: 'zip', mime: 'application/zip' };
  }

  // Text-like: valid UTF-8 (or BOM) with no NUL bytes in the sampled prefix.
  const sample = b.subarray(0, Math.min(b.length, SNIFF_BYTES));
  if (looksLikeText(sample)) {
    const head = ascii(sample, 0, 512).trimStart().toLowerCase();
    if (head.startsWith('<svg') || (head.startsWith('<?xml') && head.includes('<svg'))) {
      return { kind: 'image', container: 'svg', mime: 'image/svg+xml' };
    }
    return { kind: 'text', container: 'text', mime: 'text/plain' };
  }
  return { kind: 'unknown', container: 'unknown', mime: null };
}

function looksLikeText(b: Uint8Array): boolean {
  if (b.length === 0) return false;
  for (let i = 0; i < b.length; i++) {
    const c = b[i]!;
    if (c === 0) return false;
    if (c < 9 || (c > 13 && c < 32)) return false;
  }
  try {
    new TextDecoder('utf-8', { fatal: true }).decode(b);
    return true;
  } catch {
    // A prefix may end mid-codepoint: allow up to 3 trailing partial bytes.
    for (let cut = 1; cut <= 3; cut++) {
      try {
        new TextDecoder('utf-8', { fatal: true }).decode(b.subarray(0, b.length - cut));
        return true;
      } catch {
        /* keep trying */
      }
    }
    return false;
  }
}

/**
 * Reconcile magic-byte evidence with the declared kind. Magic wins when it is
 * definite; a "text" sniff never overrides a more specific declared text kind.
 */
export function resolveKind(sniff: Sniff, declared: MediaKind, extKind: MediaKind): MediaKind {
  if (sniff.kind !== 'unknown' && sniff.kind !== 'text' && sniff.kind !== 'archive')
    return sniff.kind;
  if (sniff.kind === 'archive') {
    return extKind === 'docx' || extKind === 'xlsx'
      ? extKind
      : declared === 'docx' || declared === 'xlsx'
        ? declared
        : 'archive';
  }
  if (sniff.kind === 'text') {
    const textual: MediaKind[] = ['markdown', 'json', 'csv', 'xml', 'text'];
    if (textual.includes(extKind)) return extKind;
    if (textual.includes(declared)) return declared;
    return 'text';
  }
  return extKind !== 'unknown' ? extKind : declared;
}
